/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Flex,
  Box,
  Text,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  InputGroup,
  InputRightAddon,
  FlexProps,
  Select,
} from "@chakra-ui/react";
import {
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { capitalize } from "lodash";
import Excel from "./../../components/excel/Excel";
import { BsPlusCircle, BsDashCircle } from "react-icons/bs";

interface IData {
  year: number | string;
  age: number | string;
  expenses: number;
  investment: number;
  wealth: number;
}

interface IPayloadItem {
  name: string;
  value: number;
  payload: any;
  color: string;
}

const RetirementCalculatorPage = () => {
  const [age, setAge] = useState<number>(27);
  const [expenses, setExpenses] = useState<number>(600000);
  const [investment, setInvestment] = useState<number>(180000);
  const [data, setData] = useState<IData[]>([]);
  const [initialInvestment, setInitialInvestment] = useState<number>(0);
  const [lifeExpectancy, setLifeExpectancy] = useState<number>(100);
  const [retirementAge, setRetirementAge] = useState<number>(60);
  const [rateOfInflation, setRateOfInflation] = useState<number>(0.06);
  const [rateOfStepUp, setRateOfStepUp] = useState<number>(0.1);
  const [rateOfActiveInterest, setRateOfActiveInterest] =
    useState<number>(0.12);
  const [rateOfPassiveInterest, setRateOfPassiveInterest] =
    useState<number>(0.06);
  const [isAdvanceOptionsOpened, setIsAdvanceOptionsOpened] =
    useState<boolean>(false);
  const [isExpensesYearly, setIsExpensesYearly] = useState<boolean>(true);
  const [isInvestmentYearly, setIsInvestmentYearly] = useState<boolean>(true);

  // Example usage:
  // const principal = 1000; // Initial amount
  // const rate = 0.05;      // Annual interest rate (5%)
  // const time = 10;        // Time in years
  // const n = 12;           // Number of times interest is compounded per year
  const calculateCompoundInterest = (
    principal: number,
    rate: number,
    time: number,
    n: number = 1
  ) => {
    return principal * Math.pow(1 + rate / n, n * time);
  };

  const calculate = (
    age: number,
    expenses: number,
    investment: number,
    initialInvestment: number = 0,
    lifeExpectancy: number = 100,
    retirementAge: number = 60,
    rateOfInflation: number = 0.06,
    rateOfStepUp: number = 0.1,
    rateOfActiveInterest: number = 0.12,
    rateOfPassiveInterest: number = 0.06
  ): IData[] => {
    const remaingEarningLife = retirementAge - age;
    const year = new Date().getFullYear();

    const data: IData[] = [
      {
        year: "",
        age: "",
        expenses: 0,
        investment: 0,
        wealth: initialInvestment,
      },
    ];

    for (let index = 0; index <= lifeExpectancy - age; index++) {
      const expensesToBe = calculateCompoundInterest(
        expenses,
        rateOfInflation,
        index
      );
      const investmentToBe = calculateCompoundInterest(
        investment,
        rateOfStepUp,
        index
      );

      if (index <= remaingEarningLife) {
        data.push({
          year: year + index,
          age: age + index,
          expenses: expensesToBe,
          investment: investmentToBe,
          wealth:
            (data[index]?.wealth ?? 0) * (1 + rateOfActiveInterest) +
            investmentToBe,
        });
      } else {
        const wealth =
          ((data[index]?.wealth ?? 0) - expensesToBe) *
          (1 + rateOfPassiveInterest);

        data.push({
          year: year + index,
          age: age + index,
          expenses: expensesToBe,
          investment: 0,
          wealth: wealth < 0 ? 0 : wealth,
        });
      }
    }

    return data;
  };

  const format = (input: number, multiplier: number = 1): string => {
    if (input === 0) return "-";
    const afterMultiply = input * multiplier;
    if (afterMultiply >= 10000000) {
      return (afterMultiply / 10000000).toFixed(2) + " Cr";
    } else if (afterMultiply >= 100000) {
      return (afterMultiply / 100000).toFixed(2) + " L";
    } else if (afterMultiply >= 1000) {
      return (afterMultiply / 1000).toFixed(2) + " K";
    } else {
      return afterMultiply.toFixed(2);
    }
  };

  const onClickHandler = (): void => {
    const data = calculate(
      age,
      expenses,
      investment,
      initialInvestment,
      lifeExpectancy,
      retirementAge,
      rateOfInflation,
      rateOfStepUp,
      rateOfActiveInterest,
      rateOfPassiveInterest
    );
    setData(data);
  };

  const RenderTooltip = ({ active, payload, label }: any): JSX.Element => {
    if (active && payload && payload.length > 0) {
      return (
        <Box
          border={"1px solid gray"}
          borderRadius={"md"}
          padding={3}
          backgroundColor={"rgba(0,0,0,0.75)"}
          textAlign={"left"}
          fontSize={"0.95rem"}
          lineHeight={"1rem"}
        >
          <Text color={"#fff"}>Age: {label}</Text>
          {payload.map((element: IPayloadItem) => (
            <Text key={element.name} color={element.color}>{`${capitalize(
              element.name
            )}: ${format(element.value)}`}</Text>
          ))}
        </Box>
      );
    }

    return <></>;
  };

  useEffect(() => {
    setData(
      calculate(
        age,
        expenses,
        investment,
        initialInvestment,
        lifeExpectancy,
        retirementAge,
        rateOfInflation,
        rateOfStepUp,
        rateOfActiveInterest,
        rateOfPassiveInterest
      )
    );
  }, [
    age,
    expenses,
    investment,
    initialInvestment,
    lifeExpectancy,
    retirementAge,
    rateOfInflation,
    rateOfStepUp,
    rateOfActiveInterest,
    rateOfPassiveInterest,
  ]);

  const inputBoxWrapperStyle: FlexProps = {
    height: "full",
    direction: "column",
    justifyContent: "end",
    gap: 1,
  };

  return (
    <>
      <Text display={{ base: "block", md: "none" }} align={"center"}>
        Please open this page on larger device.
      </Text>
      <Flex
        display={{ base: "none", md: "flex" }}
        marginY={6}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Flex width={"100%"} gap={6}>
          <Flex
            width={"40%"}
            direction={"column"}
            gap={6}
            alignItems={"center"}
          >
            <Flex gap={6}>
              <Flex {...inputBoxWrapperStyle}>
                <Text>Age</Text>
                <InputGroup alignItems={"baseline"}>
                  <Input
                    type={"number"}
                    placeholder={"Age"}
                    value={age}
                    onChange={(event) => setAge(Number(event.target.value))}
                  />
                  <InputRightAddon>years</InputRightAddon>
                </InputGroup>
              </Flex>

              <Flex {...inputBoxWrapperStyle}>
                <Text>Yearly expenses</Text>
                <Input
                  type={"number"}
                  placeholder={"Yearly expenses"}
                  value={expenses}
                  onChange={(event) => setExpenses(Number(event.target.value))}
                />
              </Flex>

              <Flex {...inputBoxWrapperStyle}>
                <Text>Yearly investment</Text>
                <Input
                  marginTop={1}
                  type={"number"}
                  placeholder={"Yearly investment"}
                  value={investment}
                  onChange={(event) =>
                    setInvestment(Number(event.target.value))
                  }
                />
              </Flex>
            </Flex>

            <Button
              variant={"ghost"}
              gap={3}
              color={"neonDefault"}
              onClick={() => setIsAdvanceOptionsOpened(!isAdvanceOptionsOpened)}
            >
              <Text>Advanced options</Text>
              {isAdvanceOptionsOpened ? <BsDashCircle /> : <BsPlusCircle />}
            </Button>

            {isAdvanceOptionsOpened && (
              <>
                <Flex gap={6}>
                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Initial investment</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        type={"number"}
                        placeholder={"Initial investment"}
                        value={initialInvestment}
                        onChange={(event) =>
                          setInitialInvestment(Number(event.target.value))
                        }
                      />
                    </InputGroup>
                  </Flex>

                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Life expectancy</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        type={"number"}
                        placeholder={"Life expectancy"}
                        value={lifeExpectancy}
                        onChange={(event) =>
                          setLifeExpectancy(Number(event.target.value))
                        }
                      />
                      <InputRightAddon>years</InputRightAddon>
                    </InputGroup>
                  </Flex>

                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Retirement age</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        type={"number"}
                        placeholder={"Retirement age"}
                        value={retirementAge}
                        onChange={(event) =>
                          setRetirementAge(Number(event.target.value))
                        }
                      />
                      <InputRightAddon>years</InputRightAddon>
                    </InputGroup>
                  </Flex>
                </Flex>

                <Flex gap={6}>
                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Rate of inflation</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        type={"number"}
                        placeholder={"Rate of inflation"}
                        value={(rateOfInflation * 100).toFixed(2)}
                        onChange={(event) =>
                          setRateOfInflation(Number(event.target.value) / 100)
                        }
                      />
                      <InputRightAddon>%</InputRightAddon>
                    </InputGroup>
                  </Flex>

                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Rate of step up</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        marginTop={1}
                        type={"number"}
                        placeholder={"Rate of step up"}
                        value={(rateOfStepUp * 100).toFixed(2)}
                        onChange={(event) =>
                          setRateOfStepUp(Number(event.target.value) / 100)
                        }
                      />
                      <InputRightAddon>%</InputRightAddon>
                    </InputGroup>
                  </Flex>

                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Rate of active interest</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        marginTop={1}
                        type={"number"}
                        placeholder={"Rate of active interest"}
                        value={(rateOfActiveInterest * 100).toFixed(2)}
                        onChange={(event) =>
                          setRateOfActiveInterest(
                            Number(event.target.value) / 100
                          )
                        }
                      />
                      <InputRightAddon>%</InputRightAddon>
                    </InputGroup>
                  </Flex>

                  <Flex {...inputBoxWrapperStyle}>
                    <Text>Rate of passive interest</Text>
                    <InputGroup alignItems={"baseline"}>
                      <Input
                        marginTop={1}
                        type={"number"}
                        placeholder={"Rate of passive interest"}
                        value={(rateOfPassiveInterest * 100).toFixed(2)}
                        onChange={(event) =>
                          setRateOfPassiveInterest(
                            Number(event.target.value) / 100
                          )
                        }
                      />
                      <InputRightAddon>%</InputRightAddon>
                    </InputGroup>
                  </Flex>
                </Flex>
              </>
            )}

            <Button onClick={onClickHandler}>Calculate</Button>
          </Flex>

          {data.length > 0 && (
            <Box width={"60%"}>
              <Tabs variant="enclosed" colorScheme="teal">
                <Flex justifyContent={"right"} alignItems={"center"} gap={6}>
                  <TabList>
                    <Tab borderRadius={"md"}>Table</Tab>
                    <Tab borderRadius={"md"}>Chart</Tab>
                  </TabList>

                  <Excel
                    name={"Returement calculation"}
                    data={{
                      "Retirement calculation": data as unknown as {
                        [key: string]: string | number;
                      }[],
                    }}
                    visibility={"download"}
                  />
                </Flex>

                <TabPanels>
                  <TabPanel>
                    <TableContainer maxHeight={500} overflowY={"auto"}>
                      <Table size="sm">
                        <Thead
                          position={"sticky"}
                          top={0}
                          zIndex={1}
                          backgroundColor={"gray.800"}
                        >
                          <Tr>
                            <Th borderRight={"1px solid"}>Year</Th>
                            <Th borderRight={"1px solid"}>Age</Th>
                            <Th borderRight={"1px solid"} isNumeric={true}>
                              <Flex
                                justifyContent={"right"}
                                alignItems={"center"}
                                flexWrap={"wrap"}
                              >
                                <Select
                                  variant="unstyled"
                                  size={"xs"}
                                  width={"fit-content"}
                                  fontWeight={"bold"}
                                  value={
                                    isExpensesYearly ? "yearly" : "monthly"
                                  }
                                  onChange={(event) => {
                                    setIsExpensesYearly(
                                      event.target.value === "yearly"
                                    );
                                  }}
                                >
                                  <option value="yearly">YEARLY</option>
                                  <option value="monthly">MONTHLY</option>
                                </Select>
                                <Text>expenses</Text>
                              </Flex>
                            </Th>
                            <Th borderRight={"1px solid"} isNumeric={true}>
                              <Flex
                                justifyContent={"right"}
                                alignItems={"center"}
                                flexWrap={"wrap"}
                              >
                                <Select
                                  variant="unstyled"
                                  size={"xs"}
                                  width={"fit-content"}
                                  fontWeight={"bold"}
                                  value={
                                    isInvestmentYearly ? "yearly" : "monthly"
                                  }
                                  onChange={(event) => {
                                    setIsInvestmentYearly(
                                      event.target.value === "yearly"
                                    );
                                  }}
                                >
                                  <option value="yearly">YEARLY</option>
                                  <option value="monthly">MONTHLY</option>
                                </Select>
                                <Text>investment</Text>
                              </Flex>
                            </Th>
                            <Th isNumeric={true}>Wealth</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {data.map((entry) => (
                            <Tr
                              borderBottomWidth={
                                entry.age === retirementAge ? 3 : 1
                              }
                            >
                              <Td>{entry.year}</Td>
                              <Td>{entry.age}</Td>
                              <Td isNumeric={true}>
                                {format(
                                  entry.expenses,
                                  isExpensesYearly ? 1 : 1 / 12
                                )}
                              </Td>
                              <Td isNumeric={true}>
                                {format(
                                  entry.investment,
                                  isInvestmentYearly ? 1 : 1 / 12
                                )}
                              </Td>
                              <Td isNumeric={true}>{format(entry.wealth)}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </TabPanel>

                  <TabPanel>
                    <Box width={"100%"} height={500}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={data}
                          margin={{
                            top: 10,
                            right: 10,
                            bottom: 10,
                            left: 10,
                          }}
                        >
                          <Tooltip content={RenderTooltip} />

                          <XAxis
                            dataKey={"age"}
                            label={{ value: "Age", position: "insideBottom" }}
                            height={50}
                          />
                          <YAxis
                            label={{
                              value: "Amount",
                              angle: -90,
                              position: "insideLeft",
                            }}
                            width={150}
                            tickCount={10}
                            tickFormatter={(value) => format(value)}
                          />

                          <ReferenceLine
                            y={10000000}
                            label="1 Cr"
                            stroke="gray"
                            strokeDasharray="5 10"
                          />
                          <ReferenceLine
                            y={100000000}
                            label="10 Cr"
                            stroke="gray"
                            strokeDasharray="5 10"
                          />
                          <ReferenceLine
                            y={250000000}
                            label="25 Cr"
                            stroke="gray"
                            strokeDasharray="5 10"
                          />
                          <ReferenceLine
                            y={500000000}
                            label="50 Cr"
                            stroke="gray"
                            strokeDasharray="5 10"
                          />
                          <ReferenceLine
                            y={1000000000}
                            label="100 Cr"
                            stroke="gray"
                            strokeDasharray="5 10"
                          />

                          <Line
                            dot={false}
                            dataKey={"expenses"}
                            stroke="#00B0FF"
                          />
                          <Line
                            dot={false}
                            dataKey={"investment"}
                            stroke="#00BFA6"
                          />
                          <Line
                            dot={false}
                            dataKey={"wealth"}
                            stroke="#F9A826"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                      <Flex
                        justifyContent={"center"}
                        alignItems={"center"}
                        gap={3}
                        paddingLeft={125}
                      >
                        <Text color={"#00B0FF"}>Expenses</Text>
                        <Text color={"#00BFA6"}>Investment</Text>
                        <Text color={"#F9A826"}>Wealth</Text>
                      </Flex>
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default RetirementCalculatorPage;
